import { AuthProvider, useAuthContext } from "@hooks/context/useAuthContext";
import { NotificationProvider } from "@hooks/context/useNotificationContext";
import { PdfDownloadProvider } from "@hooks/context/usePdfDownloadContext";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import * as Sentry from "@sentry/react";
import { PageLoader } from "@ui/molecules/Loaders/PageLoader";
import MfaSetupScreen from "@surfaces/auth/components/MfaSetupScreen";

import { getStoredIntercomThemeMode } from "@themes/intercomTheme";
import { IntercomThemeSync } from "@themes/IntercomThemeSync";
import {
  isServiceWorkerRegistrationEvent,
  parseTracesSampleRate,
  shouldDropSentryEvent
} from "@utils/sentry";
import { showToast } from "@utils/toast";
import { reportWebVitals } from "@utils/webVitals";
import { Amplify } from "aws-amplify";
// Side-effect import, required because the OAuth callback lands on "/" while
// signInWithRedirect() was called from /login. Amplify only completes an
// in-flight code exchange on a page where this listener is registered, and the
// registration is a side effect of the module that happened to import it — in
// a code-split app that is not guaranteed to be the landing page. Importing it
// at the composition root makes it unconditional. See amplify-js#14297.
import "aws-amplify/auth/enable-oauth-listener";
import { Suspense, useEffect, useMemo, useRef } from "react";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider
} from "react-router-dom";

import ErrorBoundary from "./app/components/ErrorBoundary";
import { useAppVersionRefresh } from "./hooks/utils/useAppVersionRefresh";
import {
  CATCHMENT_APP_TRACE_PROPAGATION_PATTERN,
  getAmplifyConfiguration,
  IS_PROD_ENV,
  IS_TEST_ENV,
  isLocalHost,
  isProdEnv,
  normalizeAmplifyV6Config
} from "./app/bootstrap/init";
import { getRoutes, RouteInfo } from "@app/routing/routes";
import { RootShell } from "@layout/RootShell";
import ThemeCustomization from "./themes";
import { Toast } from "./app/components/Toast";
import "react-toastify/dist/ReactToastify.css";
import "react-datasheet-grid/dist/style.css";
import "react-perfect-scrollbar/dist/css/styles.css";

import "react-18-image-lightbox/style.css";
import "./App.css";
import ErrorPage from "@app/ErrorPage";
const FORCE_APP_UPDATE_TOAST_DEBUG = false;

if (!isLocalHost()) {
  Sentry.init({
    // Drop known low-signal noise (stale-deploy chunk loads, benign browser
    // internals, extension-injected errors, expected Cognito auth failures)
    // before it reaches Sentry. See shouldDropSentryEvent for the full list.
    beforeSend(event, hint) {
      if (
        shouldDropSentryEvent(hint?.originalException) ||
        // Needs the event, not the error: the unhandled rejection from the
        // injected service-worker registration carries only the message
        // "Rejected", so it is identified by its frame.
        isServiceWorkerRegistrationEvent(event)
      ) {
        return null;
      }
      return event;
    },
    dsn:
      import.meta.env.VITE_SENTRY_DSN ||
      "https://abc088ed2240bd13b6607d58cbd2a7c1@o4507807835226112.ingest.us.sentry.io/4507807838830592",
    environment: isProdEnv() ? "production" : "development",
    integrations: [
      Sentry.browserTracingIntegration(),
      // Session replay in production only — turned off on dev/sandbox.
      ...(IS_PROD_ENV
        ? [
            Sentry.replayIntegration({
              blockAllMedia: false,
              maskAllText: false
            })
          ]
        : [])
    ],
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    // Session Replay sampling
    replaysSessionSampleRate: IS_PROD_ENV ? 0.1 : 0, // 10% of sessions in prod
    tracePropagationTargets: [
      CATCHMENT_APP_TRACE_PROPAGATION_PATTERN,
      "execute-api.ap-southeast-2.amazonaws.com",
      "localhost"
    ],
    // Tunable per env via VITE_SENTRY_TRACES_SAMPLE_RATE; defaults to 0.15. Read here
    // rather than in @utils/sentry so the parser stays free of import.meta.env,
    // which ts-jest cannot parse (same reason init.ts uses the process.env form).
    tracesSampleRate: parseTracesSampleRate(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE
    )
  });

  // Enrich error reports with this session's Web Vitals (rating + values).
  reportWebVitals();
}

// Microsoft Clarity — prod project on portal + the SAML customer shells, dev
// project on dev/the sandboxes for the staged rollout. Skipped on localhost
// (as Sentry does) and under Playwright (as the Intercom loader does), so
// local dev and E2E runs stay out of the recordings.
// ponytail: hardcoded like the Intercom app ids rather than a build-time env var.
if (!isLocalHost() && !IS_TEST_ENV) {
  import("@microsoft/clarity")
    .then(({ default: Clarity }) =>
      Clarity.init(IS_PROD_ENV ? "xxyrwmecn7" : "xxz4cidpw6")
    )
    .catch((error) => console.error("Failed to load Clarity:", error));
}

Amplify.configure(normalizeAmplifyV6Config(getAmplifyConfiguration()) as any);

const RoutedPath = (routeInfo: RouteInfo) => {
  const Layout = routeInfo.layout;
  const Component = routeInfo.component;
  return {
    element: (
      <Layout>
        <Suspense fallback={<PageLoader />}>
          <Component />
        </Suspense>
      </Layout>
    ),
    index: routeInfo.index,
    path: routeInfo.path
  };
};

function AppRouter() {
  const {
    isAuthLoading,
    isSocialProAdmin,
    loggedIn,
    mfaEnrolmentRequired,
    userProfile
  } = useAuthContext();

  // Built once per route set, NOT once per render. A router rebuilt on every
  // render orphans react-router's navigation blockers: `useBlocker` keys its
  // registration off the router instance, and on a swap it re-registers the
  // block function under the PREVIOUS key on the new router before the new key
  // lands. Only the new key is deleted when the page unmounts, so the orphan
  // stays registered and silently blocks every later navigation — the "leave
  // Project Set-Up with unsaved changes, then nothing in the app is clickable
  // until a refresh" bug. Rebuilding also throws away the router's own state,
  // so this has to be memoised regardless.
  const router = useMemo(() => {
    const routes = getRoutes(loggedIn, userProfile, isSocialProAdmin).map(
      (routeInfo) => ({
        ...RoutedPath(routeInfo),
        errorElement: (
          <Sentry.ErrorBoundary fallback={<ErrorPage />}>
            <ErrorPage />
          </Sentry.ErrorBoundary>
        )
      })
    );
    const defaultPath = routes.find((r) => r.index)?.path || "/";

    // Routes are nested under a single RootShell parent so ProfileProvider and
    // GlobalAssistant survive route changes (incl. layout swaps between
    // OrganisationLayout / ProjectDashboardLayout / SharedProjectLayout).
    return createBrowserRouter([
      {
        children: [
          ...routes,
          { element: <Navigate to={defaultPath} replace />, path: "*" }
        ],
        element: <RootShell />,
        // Without this, anything thrown by RootShell itself — the providers, or
        // the assistant before it was bounded — had no route errorElement to
        // catch it and fell through to React Router's raw default screen (#2719).
        // The app-level ErrorBoundary in App() cannot help: it sits above
        // RouterProvider, and Router catches render errors inside routes first.
        errorElement: <ErrorPage />
      }
    ]);
  }, [loggedIn, userProfile, isSocialProAdmin]);

  if (isAuthLoading) return null; // or a spinner

  // Mid-session MFA gate: a logged-in user whose org just enabled MFA (backend
  // 403 → MFA event). Force enrolment over the whole app until they complete it.
  // Rendered in the auth card shell so it's bounded like the login screen.
  if (loggedIn && mfaEnrolmentRequired) {
    return <MfaSetupScreen />;
  }

  return <RouterProvider router={router} />;
}

function App() {
  const intercomLoadedRef = useRef(false);

  // `reload` comes from useAppVersionRefresh rather than being
  // window.location.reload: reloading past a service worker that is answering
  // from a stale precache just re-serves the same bundle, and this button is
  // the one a stranded user actually presses.
  const showUpdateAvailableToast = (
    reload = () => window.location.reload()
  ) => {
    showToast(
      "Reload Application",
      undefined,
      "A new version of Catchment is available.",
      () => void reload(),
      "Reload Now",
      "update-toast",
      false
    );
  };

  useAppVersionRefresh({
    onUpdateAvailable: (reload) => showUpdateAvailableToast(reload)
  });

  useEffect(() => {
    if (FORCE_APP_UPDATE_TOAST_DEBUG) {
      showUpdateAvailableToast();
    }
  }, []);

  useEffect(() => {
    if (IS_TEST_ENV || intercomLoadedRef.current) {
      return;
    }

    const loadIntercom = async () => {
      if (intercomLoadedRef.current) {
        return;
      }
      try {
        // Dynamically import Intercom to avoid bundling it in the main bundle
        const Intercom = (await import("@intercom/messenger-js-sdk")).default;
        const appId = IS_PROD_ENV ? "ir9errip" : "lpvhvbpg";

        Intercom({
          app_id: appId,
          hide_default_launcher: true,
          theme_mode: getStoredIntercomThemeMode()
        });
        intercomLoadedRef.current = true;
      } catch (error) {
        console.error("Failed to load Intercom:", error);
      }
    };

    // Load Intercom after user interaction or after a delay, whichever comes first
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const handleInteraction = () => {
      if (!intercomLoadedRef.current) {
        loadIntercom();
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = undefined;
        }
      }
    };

    // Set up interaction listeners with 'once: true' so they auto-remove after first event
    document.addEventListener("mousemove", handleInteraction, {
      once: true,
      passive: true
    });
    document.addEventListener("scroll", handleInteraction, {
      once: true,
      passive: true
    });
    document.addEventListener("touchstart", handleInteraction, {
      once: true,
      passive: true
    });
    document.addEventListener("keydown", handleInteraction, {
      once: true,
      passive: true
    });

    // Fallback: load after 3 seconds if no interaction occurred
    timeoutId = setTimeout(() => {
      if (!intercomLoadedRef.current) {
        loadIntercom();
      }
    }, 3000);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  return (
    <ThemeCustomization>
      <IntercomThemeSync />
      <ErrorBoundary>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <NotificationProvider>
            <PdfDownloadProvider>
              <AuthProvider>
                <AppRouter />
              </AuthProvider>
              <Toast />
            </PdfDownloadProvider>
          </NotificationProvider>
        </LocalizationProvider>
      </ErrorBoundary>
    </ThemeCustomization>
  );
}

export default App;
